import { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, Euro, Building2, Filter, Search, Heart } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OffresBTPPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'Toutes' },
    { id: 'cdi', label: 'CDI' },
    { id: 'cdd', label: 'CDD' },
    { id: 'interim', label: 'Intérim' },
    { id: 'apprentissage', label: 'Apprentissage' },
  ];

  useEffect(() => {
    fetchJobs();
  }, [activeFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/jobs?type=${activeFilter}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
      }
    } catch (e) {
      console.error('Error:', e);
      // Données de démo
      setJobs([
        { id: 1, title: 'Électricien qualifié', company: 'BTP Solutions', location: 'Lyon', type: 'CDI', salary: '2500-3000€', posted: '2j' },
        { id: 2, title: 'Plombier chauffagiste', company: 'Thermik Pro', location: 'Paris', type: 'CDI', salary: '2800-3200€', posted: '3j' },
        { id: 3, title: 'Maçon expérimenté', company: 'Bâti France', location: 'Marseille', type: 'CDD', salary: '2200-2600€', posted: '1j' },
        { id: 4, title: 'Chef de chantier', company: 'Constructions Modernes', location: 'Bordeaux', type: 'CDI', salary: '3500-4000€', posted: '5j' },
        { id: 5, title: 'Apprenti carreleur', company: 'Artisan Carrelage', location: 'Toulouse', type: 'Apprentissage', salary: '1000€', posted: '1j' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Toutes les offres BTP</h1>
          <p className="text-gray-600">Découvre les meilleures opportunités du secteur</p>
        </div>

        {/* Search bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un poste, une entreprise..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              />
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Filter size={18} className="mr-2" /> Filtres
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeFilter === f.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Jobs list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <div key={job.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        job.type === 'CDI' ? 'bg-green-100 text-green-700' :
                        job.type === 'CDD' ? 'bg-blue-100 text-blue-700' :
                        job.type === 'Intérim' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {job.type}
                      </span>
                      <span className="text-sm text-gray-400">Il y a {job.posted}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{job.title}</h3>
                    <p className="text-blue-600 font-medium mb-3">{job.company}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={16} /> {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro size={16} /> {job.salary}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" size="sm">
                      <Heart size={16} />
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                      Postuler
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
